import { Link } from "react-router-dom";
import { formatIDR, formatDate } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { ArrowRight } from "lucide-react";

const STATUS_STYLES = {
  pending_payment: { bg: "#F4F4F5", text: "#71717A" },
  paid: { bg: "#E0E7FF", text: "#3730A3" },
  purchased: { bg: "#FEF3C7", text: "#92400E" },
  shipping: { bg: "#F3E8FF", text: "#6B21A8" },
  delivered: { bg: "#DCFCE7", text: "#166534" },
  released: { bg: "#F1F5F9", text: "#334155" },
  cancelled: { bg: "#FEE2E2", text: "#991B1B" },
  refunded: { bg: "#FEE2E2", text: "#991B1B" },
};

export default function OrderRow({ order, role }) {
  const { t } = useI18n();
  const status = order.escrow_status || "pending_payment";
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending_payment;
  const statusLabel = t(`order.status.${status}`);
  const counterparty = role === "buyer" ? order.jastiper_name : order.buyer_name;
  const counterpartyLabel = role === "buyer" ? t("order.role.jastiper") : t("order.role.buyer");
  const itemName = order.items?.[0]?.name || "Item";
  const moreCount = (order.items?.length || 1) - 1;

  return (
    <Link
      to={`/orders/${order.id}`}
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition-colors group"
      data-testid={`order-row-${order.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-2">
          <span
            className="label-caps px-2 py-1 font-mono text-[10px]"
            style={{ backgroundColor: s.bg, color: s.text }}
            data-testid={`order-status-${order.id}`}
          >
            {statusLabel}
          </span>
          <span className="font-mono text-xs text-zinc-400">#{order.id.slice(0, 8)}</span>
          <span className="font-mono text-xs text-zinc-400">{formatDate(order.created_at)}</span>
        </div>
        <div className="font-medium truncate">
          {itemName}
          {moreCount > 0 && <span className="text-zinc-500"> +{moreCount} {t("order.more")}</span>}
        </div>
        <div className="text-sm text-zinc-500 mt-1">
          {counterpartyLabel}: <span className="text-zinc-900">{counterparty}</span> · {t("dash.from")} {order.country}
        </div>
      </div>
      <div className="flex items-center gap-4 sm:flex-row">
        <div className="text-right">
          <div className="label-caps text-zinc-500">{t("common.total")}</div>
          <div className="font-mono font-semibold">{formatIDR(order.gross_amount)}</div>
        </div>
        <ArrowRight size={16} strokeWidth={1.5} className="text-zinc-400 group-hover:text-[#002FA7] group-hover:translate-x-1 transition-all" />
      </div>
    </Link>
  );
}
